import dayjs from 'dayjs'
import { isNil } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useFeature } from '@growthbook/growthbook-react'

import { useGroupActions, useModalState } from 'hooks'
import { PLACEHOLDER_GROUP_COVER_IMAGE } from 'lib/image'
import { ROUTE_PATH } from 'lib/paths'
import { Group } from 'lib/_types'
import { abbreviateNumber } from 'lib/helpers'
import { useGroupUserRole } from '../hooks/useGroupUserRole'
import { endpoints, useTakeOverGroupMutation } from 'store/api/yay/group'
import { useCurrentUser } from 'store/api/yay/user'
import {
  toggleGroupModeratorOfferConfirmBox,
  toggleGroupOwnerTransferConfirmBox,
} from 'store/actions/confirmBox'

import usePageModal from 'features/Modal/hooks/usePageModal'
import GroupImage from 'features/Group/GroupImage'
import { Link } from 'features/i18n/router'

import ImageLoader from 'components/ImageLoader'
import Menu from 'components/Menu'
import Button from 'components/Button'
import { MODAL_MODE } from 'components/Modal/config'
import ConfirmBoxBasic from 'components/ConfirmBoxBasic'
import { GroupName } from 'components/GroupList/components/GroupName'

const GroupHeader = ({
  group = {},
  menu,
}: {
  group: Partial<Group>
  menu: any
}) => {
  const { t } = useTranslation()

  const confirmBoxState = useModalState()
  const { data: currentUser } = useCurrentUser()
  const groupUserRole = useGroupUserRole({ groupId: group?.id })
  const { generatePageModalUrl } = usePageModal()
  const { attemptJoinGroup, GroupActionConfirmBox } = useGroupActions()
  const dispatch = useDispatch()

  const groupCategoriesRes = endpoints?.getGroupCategories?.useQueryState?.({})

  const [takeOverGroup, takeOverGroupRes] = useTakeOverGroupMutation()

  const allowOwnershipTransfer = group?.allow_ownership_transfer ?? false
  const isOwnerDie = isNil(group?.owner)
  const ownerLastLoggedIn = group?.owner?.last_loggedin_at
  const isOwnerAbsent =
    ownerLastLoggedIn &&
    dayjs().diff(dayjs(ownerLastLoggedIn * 1000), 'days') > 28

  const isEmplGiftsEnabled = useFeature('yay-17610')?.on

  const actionButton = (() => {
    if (group?.is_joined) {
      if (allowOwnershipTransfer && (isOwnerDie || isOwnerAbsent)) {
        return (
          <Button
            size="small"
            icon="account-transfer"
            desktopOnly
            onClick={() => confirmBoxState.open()}
          >
            {t('group_page.group_nav.take_over.button')}
          </Button>
        )
      }

      if (group?.pending_transfer_id == currentUser?.id) {
        return (
          <Button
            size="small"
            icon="account-transfer"
            desktopOnly
            onClick={() =>
              dispatch(
                toggleGroupOwnerTransferConfirmBox(group?.id?.toString?.())
              )
            }
          >
            {t('group_page.group_nav.owner_offer.button')}
          </Button>
        )
      }

      if (group?.pending_deputize_ids?.includes(currentUser?.id)) {
        return (
          <Button
            size="small"
            icon="account-transfer"
            desktopOnly
            onClick={() =>
              dispatch(
                toggleGroupModeratorOfferConfirmBox(group?.id?.toString?.())
              )
            }
          >
            {t('group_page.group_nav.moderator_offer.button')}
          </Button>
        )
      }

      if (isEmplGiftsEnabled) {
        return (
          <Button
            size="small"
            icon="gift"
            buttonStyle="bordered-gray-without-bg-dark-text"
            to={ROUTE_PATH.GROUP_GIFTS(group?.id)}
          >
            {t('common.gift')}
          </Button>
        )
      }
    } else {
      if (group?.is_pending) {
        return (
          <Button
            size="small"
            buttonStyle="gray"
            icon="clock"
            desktopOnly
            onClick={() => {
              attemptJoinGroup(group as Group)
            }}
          >
            {t('group_page.group_nav.join_pending_group')}
          </Button>
        )
      }

      return (
        <Button
          size="small"
          icon="account-add"
          desktopOnly
          onClick={() => {
            attemptJoinGroup(group as Group)
          }}
        >
          {t('common.join')}
        </Button>
      )
    }

    return null
  })()

  const groupCategory = groupCategoriesRes?.data?.find?.(
    (x) => x?.id == group?.group_category_id
  )

  return (
    <div className="Group__header">
      <div className="Group__header__image">
        <ImageLoader
          className="Group__header__image__wallpaper"
          url={group?.cover_image}
          alt={`${group?.topic}のカバー写真`}
          placeholderImage={PLACEHOLDER_GROUP_COVER_IMAGE}
        />

        {!isNil(group?.cover_image) && (
          <div className="Group__header__image__dimmer"></div>
        )}

        <div className="Group__header__image__floating-menu">
          {group?.pending_count > 0 &&
            (groupUserRole == 'moderator' || groupUserRole == 'owner') && (
              <Link
                to={generatePageModalUrl(MODAL_MODE.GROUP_JOIN_REQUEST, {
                  groupId: group?.id,
                })}
                className="Group__header__image__floating-menu__join-request"
              >
                {t(
                  group?.pending_count > 1
                    ? 'group_header.join_request_count_plural'
                    : 'group_header.join_request_count',
                  { number: group?.pending_count }
                )}
              </Link>
            )}

          <div className="Group__header__image__floating-menu__wrapper">
            <Link
              className="Menu__handle Menu__handle--search-white Button"
              to={ROUTE_PATH.GROUP_SEARCH_POST(group.id)}
            />

            {currentUser && (
              <Menu
                icon="ellipsis-white"
                handlerClassName="Button"
                options={menu}
              />
            )}
          </div>
        </div>

        {group?.joined_community_campaign && (
          <Link
            className="Group__header__image__ranking"
            to={generatePageModalUrl(MODAL_MODE.GROUP_IN_CIRCLE_RANKING, {
              groupId: group?.id,
            })}
          >
            <span className="Group__header__image__ranking__span">
              {t('group_page.ranking.title')}
            </span>
          </Link>
        )}

        <Link
          className="Group__header__image__icon"
          to={ROUTE_PATH.GROUP(group?.id)}
        >
          <GroupImage
            className="Group__header__image__icon__img"
            group={group as Group}
          />
        </Link>
      </div>

      {!isNil(group?.topic) && ( // To avoid seeing `0 member(s)`.
        <div className="Group__header__topic">
          <div className="Group__header__topic__top">
            <h3 className="Group__header__topic__top__h3">
              <GroupName group={group as Group} />
            </h3>

            {actionButton && (
              <div className="Group__header__topic__top__action">
                {actionButton}

                <GroupActionConfirmBox />

                <ConfirmBoxBasic
                  onClose={confirmBoxState.close}
                  isActive={confirmBoxState.isActive}
                  onOk={() => {
                    takeOverGroup({ groupId: group?.id }).then((res) => {
                      if (res?.['data']?.status == 'success') {
                        confirmBoxState?.close?.()
                      }
                    })
                  }}
                  onCancel={confirmBoxState.close}
                  confirmButtonText={t(
                    'group_page.group_nav.take_over_confirm.button'
                  )}
                  title={t('group_page.group_nav.take_over_confirm.title')}
                  content={t('group_page.group_nav.take_over_confirm.content')}
                  loading={takeOverGroupRes?.isLoading}
                />
              </div>
            )}
          </div>

          {group?.is_private && (
            <div className="Group__header__topic__private">
              <p className="Group__header__topic__private__p">
                {t('common.private_circle')}
              </p>
            </div>
          )}

          <div className="Group__header__topic__info">
            <div className="Group__header__topic__info__members">
              {t('group_header.members_count', {
                members: abbreviateNumber(group?.groups_users_count ?? 0),
              })}
            </div>

            {groupCategoriesRes?.isSuccess && !isNil(groupCategory) && (
              <div className="Group__header__topic__info__category">
                <img
                  className="Group__header__topic__info__category__img"
                  src={groupCategory?.icon}
                />

                <span className="Group__header__topic__info__category__span">
                  {groupCategory?.name}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupHeader
